import { computed, ref } from "vue"
import { toast } from "vue-sonner"
import { defineStore } from "pinia"
import type { AssetFieldsFragment, RecordFieldsFragment } from "@auger/core/gql/types"
import { uploadError } from "@auger/storage/rules"
import { useAssets } from "@/composables/useAssets"
import { client } from "@/init/apollo"
import { errorMessage } from "@/utils/errors"

// The app's upload queue, shown by UploadTray. It outlives the page that
// started an upload, so navigating away never orphans one, and a failed file
// can be retried from the tray.

/** At most this many files upload at once. */
const CONCURRENCY = 3

export interface UploadTarget {
  /** Files go under this record's folder in storage. */
  record?: Pick<RecordFieldsFragment, "id" | "type"> | null
  /** Where the file is headed, for the tray: "Acme · Board materials". */
  destination?: string | null
  /** Runs once the file is stored; a rejection marks the upload failed. */
  onUploaded?: (asset: AssetFieldsFragment) => unknown
}

export interface Upload {
  id: number
  file: File
  target: UploadTarget
  status: "queued" | "uploading" | "done" | "failed"
  progress: number
  error: string | null
  /** The storage rules refuse this file, so retrying can't help. */
  refused: boolean
  asset: AssetFieldsFragment | null
}

let nextId = 1

export const useUploadsStore = defineStore("uploads", () => {
  const uploads = ref<Upload[]>([])
  const { uploadFile } = useAssets({ client, toast })

  const active = computed(() =>
    uploads.value.filter((u) => u.status === "queued" || u.status === "uploading")
  )
  const failed = computed(() => uploads.value.filter((u) => u.status === "failed"))
  const remainingBytes = computed(() =>
    active.value.reduce((sum, u) => sum + u.file.size * (1 - u.progress / 100), 0)
  )

  function patch(id: number, changes: Partial<Upload>) {
    uploads.value = uploads.value.map((u) => (u.id === id ? { ...u, ...changes } : u))
  }

  function pump() {
    const running = uploads.value.filter((u) => u.status === "uploading").length
    const waiting = uploads.value.filter((u) => u.status === "queued")
    waiting.slice(0, Math.max(CONCURRENCY - running, 0)).forEach(start)
  }

  function start(upload: Upload) {
    patch(upload.id, { status: "uploading", progress: 0, error: null })
    uploadFile(upload.file, {
      record: upload.target.record,
      silent: true,
      onProgress: (progress: number) => patch(upload.id, { progress }),
    })
      .then(async (asset: AssetFieldsFragment) => {
        await upload.target.onUploaded?.(asset)
        patch(upload.id, { status: "done", progress: 100, asset })
      })
      .catch((err) => {
        const error = errorMessage(err)
        patch(upload.id, { status: "failed", error })
      })
      .finally(pump)
  }

  /** Queue files for upload; each is checked against the storage rules first. */
  function enqueue(files: File[], target: UploadTarget = {}): Upload[] {
    const added = files.map((file): Upload => {
      const refused = uploadError({ mimeType: file.type, size: file.size })
      return {
        id: nextId++,
        file,
        target,
        status: refused ? "failed" : "queued",
        progress: 0,
        error: refused,
        refused: !!refused,
        asset: null,
      }
    })
    uploads.value = [...uploads.value, ...added]
    pump()
    return added
  }

  /** Try a failed upload again. A file the rules refuse stays failed. */
  function retry(id: number) {
    const upload = uploads.value.find((u) => u.id === id)
    if (!upload || upload.status !== "failed" || upload.refused) return
    patch(id, { status: "queued", error: null, progress: 0 })
    pump()
  }

  function dismiss(id: number) {
    uploads.value = uploads.value.filter((u) => u.id !== id)
  }

  /** Clear everything that's finished, keeping what's still going. */
  function clearFinished() {
    uploads.value = uploads.value.filter((u) => u.status === "queued" || u.status === "uploading")
  }

  return { uploads, active, failed, remainingBytes, enqueue, retry, dismiss, clearFinished }
})
